'use client';
import React, { useEffect, useState } from 'react';
import { Tender } from '@/actions/dtos/tender/tender';
import { formatDate } from '@/utils/formatDate';
import Button from '@/components/common/Button';
import { TENDER_CODE } from '@/constants/tender';
import { faArrowUpRightFromSquare, faBrainCircuit, faLockKeyhole } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Link from 'next/link';
import { getSourcePath } from '@/rewrites';
import { useAuthenticated } from '@/hooks/useAuthenticated';
import { generateKeycloakAuthUrl } from '@/utils/keycloakUrlUtils';
import { notFound } from 'next/navigation';
import { useSelectText } from '@/hooks/useSelectText';

interface TenderPreviewCardProps {
  tender: Tender;
  showScore: boolean;
}

const TenderPreviewCard: React.FC<TenderPreviewCardProps> = ({ tender, showScore }) => {
  const { isAuthenticated, isLoading } = useAuthenticated();
  const [authUrl, setAuthUrl] = useState<string | null>(null);
  const selectTextClass = useSelectText();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      fetchAuthUrl();
    }
  }, [isLoading, isAuthenticated]);

  const fetchAuthUrl = async () => {
    const pathname = window.location.pathname;
    const updatedPathname = pathname.startsWith('/') ? pathname.slice(1) : pathname;
    const url = await generateKeycloakAuthUrl(updatedPathname);
    setAuthUrl(url);
  };

  if (!tender) {
    return notFound();
  }

  const cardContent = (
    <div className="card cursor-pointer rounded-lg bg-base-200">
      <div className="card-body flex h-full flex-col p-3 text-left sm:p-5">
        <h2 className={`card-title line-clamp-2 ${selectTextClass}`}>{tender.name}</h2>
        <p className="max-sm:hidden">
          Veröffentlicht: {formatDate(new Date(tender.details.timeline.publicationBegin))} | Angebotsfrist:{' '}
          {formatDate(new Date(tender.details.timeline.submissionEnd))}
        </p>
        <div className="mt-2 overflow-hidden text-left">
          <p className={`line-clamp-3 ${selectTextClass} text-lg`}>{tender.details.procurement.summary}</p>
        </div>
        <div className="card-actions mt-5 flex items-center justify-between">
          <Button
            label="Zur Ausschreibung"
            icon={tender.code === TENDER_CODE.UNKNOWN ? faLockKeyhole : faArrowUpRightFromSquare}
            variant="secondary"
            buttonClassName="text-white mt-2"
            titleClassName="text-base font-semibold"
          />
          {showScore && tender.score !== undefined && (
            <div className="flex items-center rounded-lg bg-base-100 px-1.5 text-base font-medium">
              <span className="text-base font-medium">
                {tender.score}%
                <FontAwesomeIcon icon={faBrainCircuit} className="ml-1 h-4 w-4" />
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (tender.code === TENDER_CODE.UNKNOWN && !isAuthenticated && authUrl) {
    return (
      <Link href={authUrl} rel="nofollow">
        <div>{cardContent}</div>
      </Link>
    );
  }

  const tenderCode = tender.code;
  const tenderSlug = tender.slug;

  return (
    <Link href={`${getSourcePath('/tenders')}/${tenderCode}/${tenderSlug}`} passHref>
      <div>{cardContent}</div>
    </Link>
  );
};

export default TenderPreviewCard;
